// services/PasskeyService.ts
// Browser-side WebAuthn wrapper for passkey operations
import { startRegistration, startAuthentication } from '@simplewebauthn/browser';
import { devLog } from '../utils/devTools';
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
  AuthenticationResponseJSON,
} from '@simplewebauthn/types';
import { api } from '../utils/api';

interface PasskeyRegistrationOptionsResponse { success: boolean; message?: string; options: PublicKeyCredentialCreationOptionsJSON; }
interface PasskeyAuthOptionsResponse { success: boolean; message?: string; options: PublicKeyCredentialRequestOptionsJSON; }
interface PasskeyMessageResponse { success: boolean; message?: string; }
interface PasskeyCheckResponse { success: boolean; hasPasskeys: boolean; }
interface PasskeyListResponse { success: boolean; message?: string; passkeys: Passkey[]; }

export interface Passkey {
  id: number;
  device_type: string | null;
  friendly_name: string | null;
  created_at: string;
  last_used_at: string | null;
  backup_eligible: boolean;
  backup_state: boolean;
}

export class PasskeyService {
  /**
   * Check if WebAuthn is supported in this browser
   */
  static isAvailable(): boolean {
    return (
      window?.PublicKeyCredential !== undefined &&
      typeof window.PublicKeyCredential === 'function'
    );
  }

  /**
   * Check if conditional UI (autofill) is supported
   * This feature allows passkeys to appear in autofill suggestions
   */
  static async isConditionalUIAvailable(): Promise<boolean> {
    if (!PasskeyService.isAvailable()) {
      devLog('[DEBUG isConditionalUIAvailable] Passkeys not available, returning false');
      return false;
    }

    // Detect Safari for logging purposes
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    devLog('[DEBUG isConditionalUIAvailable] Browser detection:', {
      userAgent: navigator.userAgent,
      isSafari,
      safariVersion: navigator.userAgent.match(/Version\/([\d.]+)/)?.[1]
    });

    try {
      // Type assertion needed as isConditionalMediationAvailable is not in standard TS lib yet
      const pkc = window.PublicKeyCredential;
      if (typeof pkc.isConditionalMediationAvailable === 'function') {
        const available = await pkc.isConditionalMediationAvailable();
        devLog('[DEBUG isConditionalUIAvailable] isConditionalMediationAvailable returned:', available);

        if (isSafari && available) {
          devLog('[DEBUG isConditionalUIAvailable] Safari with conditional UI enabled - will use fallback detection for stuck promises');
        }

        return available || false;
      }
      devLog('[DEBUG isConditionalUIAvailable] isConditionalMediationAvailable not a function, returning false');
      return false;
    } catch (error) {
      console.error('[DEBUG isConditionalUIAvailable] Error:', error);
      return false;
    }
  }

  /**
   * Register a new passkey for the authenticated user
   *
   * @param friendlyName - Optional user-friendly name for the passkey (e.g., "My iPhone", "Work Laptop")
   * @returns Result object with success status and message
   */
  static async registerPasskey(friendlyName?: string): Promise<{ success: boolean; message: string }> {
    const optionsResult = await PasskeyService.fetchRegistrationOptions();
    if (!optionsResult.success || !optionsResult.options) {
      return { success: false, message: optionsResult.message || 'Failed to start registration' };
    }
    return PasskeyService.completeRegistration(optionsResult.options, friendlyName);
  }

  /**
   * Fetch registration options from the server.
   *
   * Safari/WebKit requires navigator.credentials.create() to run under transient
   * user activation. An awaited network request (this call) between the user's
   * click and startRegistration() consumes that activation, so Safari never
   * presents the passkey sheet and the create() promise never settles. Callers
   * MUST fetch options ahead of the final click (e.g. when the dialog opens) and
   * then call completeRegistration() synchronously from the click handler.
   *
   * @returns Result with registration options on success
   */
  static async fetchRegistrationOptions(): Promise<{ success: boolean; options?: PublicKeyCredentialCreationOptionsJSON; message?: string }> {
    try {
      if (!PasskeyService.isAvailable()) {
        return { success: false, message: 'Passkeys are not supported on this device' };
      }

      const optionsResponse = await api.post<PasskeyRegistrationOptionsResponse>('/api/passkeys/registration/options', {});
      if (!optionsResponse.success) {
        return { success: false, message: optionsResponse.message || 'Failed to start registration' };
      }

      return { success: true, options: optionsResponse.options };
    } catch (error) {
      console.error('Passkey registration options error:', error);
      return { success: false, message: 'An error occurred during registration' };
    }
  }

  /**
   * Run the WebAuthn registration ceremony with pre-fetched options and verify
   * the result with the server. Call this directly from a click handler with
   * options obtained earlier via fetchRegistrationOptions() so no awaited network
   * request sits between the user gesture and startRegistration() (Safari).
   *
   * @param options - Registration options previously fetched from the server
   * @param friendlyName - Optional friendly name for the passkey
   * @returns Result object with success status and message
   */
  static async completeRegistration(
    options: PublicKeyCredentialCreationOptionsJSON,
    friendlyName?: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      // Prompt user to create credential
      let registrationResponse: RegistrationResponseJSON;
      try {
        registrationResponse = await startRegistration({ optionsJSON: options });
      } catch (error) {
        // User cancelled or other error
        if (error instanceof Error && error.name === 'NotAllowedError') {
          return { success: false, message: 'Registration was cancelled' };
        }
        // Authenticator already holds a passkey for this account (excludeCredentials)
        if (error instanceof Error && error.name === 'InvalidStateError') {
          return { success: false, message: 'This device already has a passkey for your account.' };
        }
        console.error('Registration error:', error);
        return { success: false, message: 'Failed to create passkey' };
      }

      // Send credential to server for verification
      const verifyResponse = await api.post<PasskeyMessageResponse>('/api/passkeys/registration/verify', {
        response: registrationResponse,
        friendlyName,
      });

      if (!verifyResponse.success) {
        return { success: false, message: verifyResponse.message || 'Failed to verify passkey' };
      }

      return { success: true, message: 'Passkey registered successfully' };
    } catch (error) {
      console.error('Passkey registration error:', error);
      return { success: false, message: 'An error occurred during registration' };
    }
  }

  /**
   * Authenticate using a passkey
   *
   * @param email - Reporter's email address (used to identify which passkeys to use)
   * @returns Result object with success status and message
   */
  static async authenticateWithPasskey(email: string): Promise<{ success: boolean; message: string }> {
    try {
      if (!PasskeyService.isAvailable()) {
        return { success: false, message: 'Passkeys are not supported on this device' };
      }

      // Get authentication options from server
      const optionsResponse = await api.post<PasskeyAuthOptionsResponse>('/api/passkeys/authentication/options', { email });
      if (!optionsResponse.success) {
        return { success: false, message: optionsResponse.message || 'Failed to start authentication' };
      }

      const options: PublicKeyCredentialRequestOptionsJSON = optionsResponse.options;

      // Prompt user to authenticate
      let authResponse: AuthenticationResponseJSON;
      try {
        authResponse = await startAuthentication({ optionsJSON: options });
      } catch (error) {
        // User cancelled or other error
        if (error instanceof Error && error.name === 'NotAllowedError') {
          return { success: false, message: 'Authentication was cancelled' };
        }
        console.error('Authentication error:', error);
        return { success: false, message: 'Failed to authenticate with passkey' };
      }

      // Send assertion to server for verification
      const verifyResponse = await api.post<PasskeyMessageResponse>('/api/passkeys/authentication/verify', {
        email,
        response: authResponse,
      });

      if (!verifyResponse.success) {
        return { success: false, message: verifyResponse.message || 'Authentication failed' };
      }

      return { success: true, message: 'Authentication successful' };
    } catch (error) {
      console.error('Passkey authentication error:', error);
      return { success: false, message: 'An error occurred during authentication' };
    }
  }

  /**
   * Start conditional UI (autofill) passkey authentication
   * This allows passkeys to appear in the browser's autofill dropdown
   *
   * @param onSuccess - Callback when authentication succeeds
   * @param onError - Callback when authentication fails
   * @returns Abort controller to cancel the request
   */
  static async startConditionalAuthentication(
    onSuccess: (response: AuthenticationResponseJSON) => void,
    onError: (error: string) => void
  ): Promise<AbortController | null> {
    try {
      devLog('[DEBUG PasskeyService] Starting conditional authentication...');

      if (!PasskeyService.isAvailable()) {
        devLog('[DEBUG PasskeyService] Passkeys not available');
        return null;
      }

      const conditionalUIAvailable = await PasskeyService.isConditionalUIAvailable();
      devLog('[DEBUG PasskeyService] Conditional UI available:', conditionalUIAvailable);

      if (!conditionalUIAvailable) {
        return null;
      }

      // Get authentication options without specifying an email
      // Server should return options that work for any passkey on this domain
      const optionsResponse = await api.post<PasskeyAuthOptionsResponse>('/api/passkeys/authentication/options/conditional', {});
      devLog('[DEBUG PasskeyService] Options response:', optionsResponse);

      if (!optionsResponse.success) {
        console.error('[DEBUG PasskeyService] Failed to get options:', optionsResponse);
        return null;
      }

      const options: PublicKeyCredentialRequestOptionsJSON = optionsResponse.options;

      // Create abort controller to cancel if user navigates away
      const abortController = new AbortController();

      // Start authentication with conditional UI (autofill)
      // NOTE: Do NOT pass the abort signal - conditional UI shouldn't be abortable
      // The browser manages the lifecycle of conditional authentication
      devLog('[DEBUG PasskeyService] About to call startAuthentication with:', {
        optionsJSON: options,
        useBrowserAutofill: true
      });

      let authPromise;
      try {
        authPromise = startAuthentication({
          optionsJSON: options,
          useBrowserAutofill: true,
        });
      } catch (error) {
        console.error('[DEBUG PasskeyService] startAuthentication threw synchronous error:', error);
        throw error;
      }

      authPromise
        .then((authResponse) => {
          devLog('[DEBUG PasskeyService] Conditional auth SUCCESS, calling onSuccess callback');
          onSuccess(authResponse);
        })
        .catch((error) => {
          devLog('[DEBUG PasskeyService] Conditional auth promise REJECTED');
          console.error('[DEBUG PasskeyService] Error details:', {
            name: error.name,
            message: error.message,
            stack: error.stack,
            error: error
          });

          // Safari-specific: Silently handle NotAllowedError which can happen on page refresh
          // This is a known Safari/WebKit bug that causes alternating errors
          if (error.name === 'NotAllowedError') {
            devLog('[DEBUG PasskeyService] NotAllowedError (likely Safari bug on refresh) - silently ignoring');
            // Don't call onError - let the user continue with email flow
            return;
          }

          // Ignore AbortError (user cancelled or navigated away)
          if (error.name === 'AbortError') {
            devLog('[DEBUG PasskeyService] Conditional auth aborted (user cancelled or navigated)');
            return;
          }

          // Silently handle "No input with webauthn" error - DOM timing issue
          // This can happen on page load/redirect when the input isn't ready yet
          if (error.message && error.message.includes('No <input>') && error.message.includes('webauthn')) {
            devLog('[DEBUG PasskeyService] DOM timing issue - webauthn input not ready yet, silently ignoring');
            // Don't call onError - this is expected on some page loads and email+OTP still works
            return;
          }

          // Other errors - report to caller
          console.error('[DEBUG PasskeyService] Conditional authentication error:', error);
          onError(error.message || 'Authentication failed');
        });

      devLog('[DEBUG PasskeyService] Conditional authentication listening for passkey selection...');
      return abortController;
    } catch (error) {
      console.error('[DEBUG PasskeyService] Error starting conditional authentication:', error);
      return null;
    }
  }

  /**
   * Verify conditional authentication response
   * Used when user selects a passkey from autofill
   *
   * @param response - AuthenticationResponseJSON from browser
   * @returns Result object with success status and message
   */
  static async verifyConditionalAuthentication(
    response: AuthenticationResponseJSON
  ): Promise<{ success: boolean; message: string }> {
    try {
      devLog('[DEBUG verifyConditionalAuthentication] Sending verify request to server...');
      // Send assertion to server for verification (without email)
      const verifyResponse = await api.post<PasskeyMessageResponse>('/api/passkeys/authentication/verify', {
        response: response,
      });

      devLog('[DEBUG verifyConditionalAuthentication] Server response:', verifyResponse);

      if (!verifyResponse.success) {
        return { success: false, message: verifyResponse.message || 'Authentication failed' };
      }

      return { success: true, message: 'Authentication successful' };
    } catch (error) {
      console.error('[DEBUG verifyConditionalAuthentication] Error:', error);
      return { success: false, message: 'An error occurred during authentication' };
    }
  }

  /**
   * Check if an email has registered passkeys
   *
   * @param email - Email address to check
   * @returns True if the email has passkeys, false otherwise
   */
  static async hasPasskeys(email: string): Promise<boolean> {
    try {
      const response = await api.get<PasskeyCheckResponse>(`/api/passkeys/check?email=${encodeURIComponent(email)}`);
      return response.success && response.hasPasskeys;
    } catch (error) {
      console.error('Error checking passkeys:', error);
      return false;
    }
  }

  /**
   * List all passkeys for the authenticated user
   *
   * @returns Array of passkey objects
   */
  static async listPasskeys(): Promise<Passkey[]> {
    try {
      const response = await api.get<PasskeyListResponse>('/api/passkeys/list');
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch passkeys');
      }
      return response.passkeys;
    } catch (error) {
      console.error('Error listing passkeys:', error);
      return [];
    }
  }

  /**
   * Delete a passkey
   *
   * @param passkeyId - ID of the passkey to delete
   * @returns Result object with success status and message
   */
  static async deletePasskey(passkeyId: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.delete<PasskeyMessageResponse>(`/api/passkeys/${passkeyId}`, {});
      return {
        success: response.success,
        message: response.message || (response.success ? 'Passkey deleted' : 'Failed to delete passkey'),
      };
    } catch (error) {
      console.error('Error deleting passkey:', error);
      return { success: false, message: 'An error occurred while deleting the passkey' };
    }
  }

  /**
   * Rename a passkey
   *
   * @param passkeyId - ID of the passkey to rename
   * @param friendlyName - New friendly name for the passkey
   * @returns Result object with success status and message
   */
  static async renamePasskey(passkeyId: number, friendlyName: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.patch<PasskeyMessageResponse>(`/api/passkeys/${passkeyId}/name`, { friendlyName });
      return {
        success: response.success,
        message: response.message || (response.success ? 'Passkey renamed' : 'Failed to rename passkey'),
      };
    } catch (error) {
      console.error('Error renaming passkey:', error);
      return { success: false, message: 'An error occurred while renaming the passkey' };
    }
  }
}
